import { ArrowRight } from 'lucide-react';
import type { ActualsResponse, KpiCatalogueEntry, OpenEvidence, PersonActuals } from '../../types/strategicKpis';
import { CATEGORY_LABEL, CATEGORY_ORDER, formatKpiValue, judgedOnYtdShare, kpiLabel, noDataText, priorDirection } from '../../utils/strategicKpiFormat';
import { formatDate } from '../../utils/format';
import { judgePerson, lightOf, paceAtYearEnd } from '../../utils/strategicKpiSelectors';
import { PriorCue, StatusBadge } from './parts';
import { SidePanel } from './shell/SidePanel';

interface Props {
    data: ActualsResponse;
    person: PersonActuals;
    /** The steered KPIs of the page (`steeredKpis(data)`); BAB is left out — it has no person grain. */
    steered: KpiCatalogueEntry[];
    onOpenEvidence: OpenEvidence;
    /** «Edit this person's targets» — B4 switches to Targets with this person (Targets is phase C). */
    onEditTargets?: (staffId: string) => void;
    onClose: () => void;
}

/** The figure the person is judged against today: visits and GP amounts the YTD share, every other KPI the year target. */
function targetToDate(kpi: KpiCatalogueEntry, j: ReturnType<typeof judgePerson>): number | null {
    return judgedOnYtdShare(kpi) ? j.targetYtd : j.target;
}

/**
 * The person panel of People (masterplan skpi-redesign E16, package B3): one person, every steered KPI
 * under its family — actual, target to date, achieved with its light, the prior year neutral (a value and
 * the PriorCue, never a difference, §2 rule 8). A click on an actual opens its evidence (E9). Content of the
 * page's SidePanel (A2a): B4 renders it in the panel slot and sets `.panel-open` on the root.
 *
 * No logic of its own: the values are `judgePerson` of the payload, formatted by the same formatters as the
 * ranking and the steered rows.
 */
export function PersonPanel({ data, person, steered, onOpenEvidence, onEditTargets, onClose }: Props) {
    const kpis = steered.filter(k => k.category !== 'bab');
    const families = [...CATEGORY_ORDER, ...[...new Set(kpis.map(k => k.category))].filter(c => !CATEGORY_ORDER.includes(c))]
        .map(f => ({ f, list: kpis.filter(k => k.category === f) }))
        .filter(x => x.list.length > 0);
    const sub = `${person.teamManagerName ?? 'no team'} · ${person.companyCode} · ${data.year} as of ${formatDate(data.asOf)}${person.isActive ? '' : ' · left'}`;

    return (
        <SidePanel
            title={person.fullName}
            sub={sub}
            onClose={onClose}
            contentKey={person.staffId}
            closeLabel="Close the person"
            testId="skpi-person-panel"
            footer={onEditTargets && (
                <button type="button" className="btn secondary sm" onClick={() => onEditTargets(person.staffId)} data-testid="skpi-person-edit-targets">
                    Edit this person's targets <ArrowRight className="i sm" aria-hidden="true" />
                </button>
            )}
        >
            {families.length === 0 && <p className="muted">No KPI has a target for {data.year} in this selection.</p>}
            {families.map(({ f, list }) => (
                <section key={f} aria-label={CATEGORY_LABEL[f] ?? f}>
                    <h3 className="sect">{CATEGORY_LABEL[f] ?? f}</h3>
                    <table className="tbl compact tbl-fixed" style={{ marginTop: 6 }}>
                        <colgroup><col /><col style={{ width: 92 }} /><col style={{ width: 120 }} /></colgroup>
                        <thead>
                            <tr><th>KPI</th><th className="n">Actual</th><th>Target to date · achieved</th></tr>
                        </thead>
                        <tbody>
                            {list.map(k => <PersonKpiRow key={k.kpiKey} data={data} person={person} kpi={k} onOpenEvidence={onOpenEvidence} />)}
                        </tbody>
                    </table>
                </section>
            ))}
        </SidePanel>
    );
}

function PersonKpiRow({ data, person, kpi, onOpenEvidence }: { data: ActualsResponse; person: PersonActuals; kpi: KpiCatalogueEntry; onOpenEvidence: OpenEvidence }) {
    const j = judgePerson(person.values[kpi.kpiKey]);
    const atYearEnd = paceAtYearEnd(kpi, data.windows.yearFraction);
    const actual = j.actual === null ? 'no data' : formatKpiValue(kpi.unit, j.actual);
    const name = `Rows behind ${kpiLabel(kpi)} of ${person.fullName}`;
    const prior = j.prior === null ? null : formatKpiValue(kpi.unit, j.prior);
    const target = targetToDate(kpi, j);
    return (
        <tr data-skpi-panel-kpi={kpi.kpiKey}>
            <td title={kpi.definitionText}>
                <div className="cellstack">
                    <span className="name">{kpiLabel(kpi)}</span>
                    <span className="sub2">{prior === null ? 'no prior year' : <>PY {prior}<PriorCue dir={priorDirection(kpi, j.actual, j.prior)} /></>}</span>
                </div>
            </td>
            <td className="n">
                <button
                    type="button"
                    className="lnk tnum"
                    style={{ fontSize: 'inherit' }}
                    onClick={() => onOpenEvidence(kpi.kpiKey, 'PERSON', person.staffId, person.fullName)}
                    aria-label={`${actual} — ${name}`}
                    title={j.actual === null ? `${noDataText(j.noData)} — ${name}` : name}
                >
                    {actual}
                </button>
            </td>
            <td>
                <div className="cellstack">
                    <span className="tnum">{j.hasTarget ? formatKpiValue(kpi.unit, target) : <span className="dash">no target</span>}</span>
                    <span><StatusBadge light={lightOf(j)} achievement={j.achievement} hasTarget={j.hasTarget} noData={j.noData} atYearEnd={atYearEnd} /></span>
                </div>
            </td>
        </tr>
    );
}
